import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import SEO from '../components/SEO';
import { PAGE_SEO } from '../utils/pageSeo';

const Contact = () => {
  const { t } = useTranslation('contact');
  const { language } = useLanguage();
  const seo = PAGE_SEO.contact;
  const location = useLocation();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    message: '',
    website: '' // Honeypot field
  });

  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [formStartTime] = useState(Date.now());

  // Auto-scroll to INFORMATION panel when hash is present
  useEffect(() => {
    if (location.hash === '#information') {
      setTimeout(() => {
        const element = document.getElementById('information-panel');
        if (element) {
          const headerOffset = 100; // Account for sticky header
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
          
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, 300); // Delay to ensure page is fully rendered
    }
  }, [location.hash]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
    // Clear error when user types
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Name validation: min 2 chars, letters and spaces only
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData.name.trim().length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    } else if (!/^[a-zA-Z\s'-]+$/.test(formData.name)) {
      newErrors.name = 'Name can only contain letters, spaces, hyphens and apostrophes';
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    } else if (formData.email.length > 100) {
      newErrors.email = 'Email is too long';
    }

    // Message validation: min 10 chars
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 10) {
      newErrors.message = 'Message must be at least 10 characters';
    } else if (formData.message.length > 1000) {
      newErrors.message = 'Message is too long (max 1000 characters)';
    }

    // Spam detection: Honeypot field should be empty
    if (formData.website) {
      newErrors.spam = 'Spam detected';
      return newErrors;
    }

    // Spam detection: Form filled too quickly (less than 3 seconds)
    const timeTaken = (Date.now() - formStartTime) / 1000;
    if (timeTaken < 3) {
      newErrors.spam = 'Please take your time to fill the form';
      return newErrors;
    }

    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    const validationErrors = validateForm();
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      // Scroll to first error
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.getElementById(firstErrorField);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
      return;
    }

    // Submit form to backend
    fetch('/contact-handler.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(formData)
    })
    .then(response => {
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      return response.json();
    })
    .then(data => {
      if (data.success) {
        setSubmitted(true);
        // Reset after 5 seconds
        setTimeout(() => {
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            message: '',
            website: ''
          });
          setErrors({});
          setSubmitted(false);
        }, 5000);
      } else {
        setErrors({ submit: data.message || 'Failed to send message. Please try again.' });
      }
    })
    .catch(error => {
      console.error('Error:', error);
      // Check if it's a development environment issue
      if (import.meta.env.DEV && error.message.includes('Unexpected token')) {
        setErrors({ submit: 'Development mode: XAMPP must be running on port 80 to test PHP handler. Form will work in production.' });
      } else {
        setErrors({ submit: 'Network error. Please try again.' });
      }
    });
  };

  return (
    <div className="contact-page">
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        canonicalPath={`/${language}/contact`}
        structuredData={{
          "@context": "https://schema.org",
          "@type": "ContactPage",
          "mainEntity": {
            "@type": "Organization",
            "name": "EWANDS SDN BHD",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "13-6, Block C1, Jalan PJU 1/41, Dataran Prima",
              "addressLocality": "Petaling Jaya",
              "addressRegion": "Selangor",
              "postalCode": "47301",
              "addressCountry": "MY"
            }
          }
        }}
      />
      {/* Hero Section */}
      <section className="hero-section contact-hero">
        <div className="hero-background">
          <div className="gradient-layer"></div>
          <div className="particle-layer"></div>
          <div className="grid-overlay"></div>
          <div className="tech-lines"></div>
        </div>
        
        <div className="hero-content">
          <div className="container">
            <div className="hero-wrapper">
              <div className="tech-element tech-element-2"></div>
              <div className="tech-element tech-element-6"></div>
              <div className="tech-element tech-element-7"></div>
              <h1 className="hero-quote">
                <span className="quote-highlight">{t('hero.title')}</span><br />
                {t('hero.subtitle1')}<br />
                {t('hero.subtitle2')}
              </h1>
              <p className="hero-subtitle fade-in" style={{ animationDelay: '0.2s' }}>
                {t('hero.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Content */}
      <section className="capabilities-grid-section pb-5 mb-5">
        <div className="container">
          <div className="row gy-5">
            <div className="col-lg-5">
              <div id="information-panel" className="capability-card-modern fade-in">
                <div className="card-tag">{t('info.tag')}</div>
                <h2 className="capability-title-bold">{t('info.heading')}</h2>
                <div className="card-divider"></div>
                
                <div className="contact-method mb-4">
                  <h4 style={{ fontWeight: 800, color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <rect width="20" height="16" x="2" y="4" rx="2"/><path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                    </svg>
                    {t('info.email.title')}
                  </h4>
                  <p className="capability-desc-text">{t('info.email.value')}</p>
                </div>
                
                <div className="contact-method mb-4">
                  <h4 style={{ fontWeight: 800, color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
                    </svg>
                    {t('info.phone.title')}
                  </h4>
                  <p className="capability-desc-text">{t('info.phone.value')}</p>
                </div>
                
                <div className="contact-method">
                  <h4 style={{ fontWeight: 800, color: 'var(--brand-primary)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z"/><circle cx="12" cy="10" r="3"/>
                    </svg>
                    {t('info.office.title')}
                  </h4>
                  <p className="capability-desc-text">
                    {t('info.office.address1')}<br />
                    {t('info.office.address2')}
                  </p>
                  <div className="d-flex flex-column gap-3 mt-4">
                    <a 
                      href="https://waze.com/ul?q=Ewands%20Sdn%20Bhd%20Dataran%20Prima" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hero-cta w-fit-content"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {t('info.office.wazeButton')}
                      <span className="cta-chevrons">
                        <span>›</span><span>›</span><span>›</span>
                      </span>
                    </a>
                    <a 
                      href="https://www.google.com/maps/search/?api=1&query=Ewands%20Sdn%20Bhd%20Dataran%20Prima" 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="hero-cta w-fit-content"
                      style={{ fontSize: '0.85rem' }}
                    >
                      {t('info.office.mapsButton')}
                      <span className="cta-chevrons">
                        <span>›</span><span>›</span><span>›</span>
                      </span>
                    </a>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-lg-7">
              <div className="capability-card-modern fade-in" style={{ animationDelay: '0.2s' }}>
                {submitted ? (
                  <div className="text-center fade-in py-5">
                    <div className="card-tag">{t('form.success.tag')}</div>
                    <h2 className="capability-title-bold" style={{ textAlign: 'center' }}>{t('form.success.title')}</h2>
                    <div className="card-divider" style={{ margin: '0 auto 2rem' }}></div>
                    <p className="capability-desc-text">{t('form.success.message')}</p>
                  </div>
                ) : (
                  <>
                    <div className="card-tag">{t('form.tag')}</div>
                    <h2 className="capability-title-bold">{t('form.heading')}</h2>
                    <div className="card-divider"></div>
                    <form onSubmit={handleSubmit}>
                      {/* Honeypot field - hidden from users */}
                      <input 
                        type="text" 
                        name="website" 
                        value={formData.website}
                        onChange={handleChange}
                        style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px' }}
                        tabIndex="-1"
                        autoComplete="off"
                        aria-hidden="true"
                      />

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="name" className="form-label">{t('form.name')} {t('form.required')}</label>
                          <input 
                            type="text" 
                            className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                            id="name" 
                            name="name" 
                            value={formData.name} 
                            onChange={handleChange}
                            autoComplete="name"
                          />
                          {errors.name && <div className="invalid-feedback d-block">{errors.name}</div>}
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="email" className="form-label">{t('form.email')} {t('form.required')}</label>
                          <input 
                            type="email" 
                            className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                            id="email" 
                            name="email" 
                            value={formData.email} 
                            onChange={handleChange}
                            autoComplete="email"
                          />
                          {errors.email && <div className="invalid-feedback d-block">{errors.email}</div>}
                        </div>
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label htmlFor="company" className="form-label">{t('form.company')}</label>
                          <input 
                            type="text" 
                            className="form-control" 
                            id="company" 
                            name="company" 
                            value={formData.company} 
                            onChange={handleChange}
                            autoComplete="organization"
                          />
                        </div>
                        <div className="col-md-6 mb-3">
                          <label htmlFor="phone" className="form-label">{t('form.phone')}</label>
                          <input 
                            type="tel" 
                            className="form-control" 
                            id="phone" 
                            name="phone" 
                            value={formData.phone} 
                            onChange={handleChange}
                            autoComplete="tel"
                          />
                        </div>
                      </div>

                      <div className="mb-4">
                        <label htmlFor="message" className="form-label">{t('form.message')} {t('form.required')}</label>
                        <textarea 
                          className={`form-control ${errors.message ? 'is-invalid' : ''}`}
                          id="message" 
                          name="message" 
                          rows="4" 
                          value={formData.message} 
                          onChange={handleChange}
                        ></textarea>
                        {errors.message && <div className="invalid-feedback d-block">{errors.message}</div>}
                      </div>

                      {(errors.spam || errors.submit) && (
                        <div className="alert alert-danger mb-3" role="alert">
                          {errors.spam || errors.submit}
                        </div>
                      )}

                      <button type="submit" className="enquiry-submit-btn">
                        {t('form.submit')}
                        <span className="cta-chevrons">
                          <span>›</span><span>›</span><span>›</span>
                        </span>
                      </button>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
